import { FC, useState } from 'react';
import Image from 'next/image';
import { IoAlertCircle } from 'react-icons/io5';

import { Box, Button, Flex, Icon, Stack, useToast } from '@chakra-ui/react';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, PaymentMethod, StripeCardElementChangeEvent } from '@stripe/stripe-js';

import { getCardIcon } from '~/features/settings/utils';
import { CardBrand } from '~/types';

if (!process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY) {
  throw new Error('Missing env var: NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY');
}

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY);

export const StripePaymentMethod: FC<{
  loading?: boolean;
  paymentMethod: PaymentMethod | undefined;
  cardDeclined?: boolean;
  onAddPaymentMethod: (paymentMethod: PaymentMethod) => void;
  onRemovePaymentMethod: () => void;
}> = ({ loading, paymentMethod, cardDeclined, onAddPaymentMethod, onRemovePaymentMethod }) => {
  return (
    <Elements stripe={stripePromise}>
      {paymentMethod ? (
        <Flex
          justifyContent='space-between'
          alignItems='center'
          p={4}
          border='1px'
          borderColor='light-gray'
          borderRadius='md'
          gap={4}
          flexDir={{ base: 'column', sm: 'row' }}
          color={cardDeclined ? 'red' : 'inherit'}
        >
          <Flex gap={2} alignItems='center' flexDir={{ base: 'column', sm: 'row' }}>
            <Icon
              as={cardDeclined ? IoAlertCircle : getCardIcon((paymentMethod.card?.brand as CardBrand) ?? 'unknown')}
              w={8}
              h={8}
            />
            {paymentMethod.card?.brand} card ending in {paymentMethod.card?.last4}
          </Flex>
          <Flex gap={4}>
            <Button variant='outline' colorScheme='red' size='sm' onClick={onRemovePaymentMethod} isLoading={loading}>
              Remove card
            </Button>
          </Flex>
        </Flex>
      ) : (
        <CardInput loading={loading} onAddPaymentMethod={onAddPaymentMethod} />
      )}
    </Elements>
  );
};

export const CardInput: FC<{
  loading?: boolean;
  onAddPaymentMethod: (paymentMethod: PaymentMethod) => void;
}> = ({ loading, onAddPaymentMethod }) => {
  const toast = useToast();

  const stripe = useStripe();

  const elements = useElements();

  const [isLoading, setIsLoading] = useState(false);

  const [cardInputValid, setCardInputValid] = useState(false);

  const handleCardSave = async () => {
    if (!stripe) {
      throw new Error('`stripe` is undefined when saving card');
    }

    if (!elements) {
      throw new Error('Stripe `elements` is undefined when saving card');
    }

    const card = elements.getElement(CardElement);

    if (!card) {
      throw new Error('card is undefined when saving card');
    }

    setIsLoading(true);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card,
    });

    setIsLoading(false);

    if (!paymentMethod || error) {
      toast({
        title: 'An error occurred',
        description: error.message ? error.message : 'Error creating payment method, please try again',
        status: 'error',
        isClosable: true,
      });

      throw new Error(error.message ? error.message : 'Error creating payment method');
    }

    onAddPaymentMethod(paymentMethod);
  };

  const handleCardChange = (event: StripeCardElementChangeEvent) => {
    const { complete } = event;
    setCardInputValid(complete);
  };

  return (
    <Stack w='full' maxW='sm' m='auto'>
      <Box border='1px' borderColor='light-gray' borderRadius='md' p={4}>
        <CardElement onChange={handleCardChange} />
      </Box>
      <Button
        colorScheme='light-blue'
        color='medium-gray'
        size='sm'
        disabled={!cardInputValid || isLoading || loading}
        isLoading={isLoading || loading}
        onClick={handleCardSave}
      >
        Save card
      </Button>
      <Box alignSelf='center' opacity={0.7}>
        <Image src='/images/powered-by-stripe.svg' width={89} height={20} alt='Powered by Stripe' />
      </Box>
    </Stack>
  );
};
